import { lazy, Suspense, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CalendarDays, Clock, Check, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { useCachedGet } from "@/lib/cache";
import { useAuth } from "@/context/AuthContext";
import { bookingServices } from "@/lib/bookingServices";
import { daysLabel, fmtTime } from "@/components/post/HoursPicker";

const BookingForm = lazy(() => import("@/components/listing/BookingForm").then((m) => ({ default: m.BookingForm })));

const Step = ({ n, title, children }) => (
  <div className="space-y-2.5">
    <p className="flex items-center gap-2 text-sm font-extrabold text-slate-900"><span className="w-6 h-6 rounded-full bg-[#1A5C3A] text-white text-xs flex items-center justify-center">{n}</span>{title}</p>
    {children}
  </div>
);

// One box per category (cats / dogs / packages…); categories are independent, the one-option-type rule applies inside each box.
function ServicePicker({ services, picked, toggle, L }) {
  const boxes = [];
  services.forEach((s) => { const last = boxes[boxes.length - 1]; if (last && last.name === s.groupLabel) last.items.push(s); else boxes.push({ name: s.groupLabel, items: [s] }); });
  const named = boxes.filter((x) => x.name).length;
  return (
    <div className="space-y-3" data-testid="booking-services">
      {named > 1 && <p className="text-xs text-slate-500" data-testid="booking-multi-hint">{L("يمكنك اختيار خدمات من أكثر من فئة في نفس الحجز. داخل كل فئة اختر: خدمات فردية أو سعر جميع الخدمات أو العرض الحصري.", "You can pick services from more than one category in the same booking. Within each category choose: individual services, all-services price, or the exclusive offer.")}</p>}
      {boxes.map((box, bi) => (
        <div key={bi} className={box.name && named > 1 ? "rounded-2xl border border-[#EFEBE0] bg-[#FAF8F3] p-3 space-y-2" : "space-y-2"} data-testid={`booking-group-${box.items[0].group || bi}`}>
          {box.name && named > 1 && <p className="text-sm font-extrabold text-slate-900">{box.name}</p>}
          <ServiceItems items={box.items} services={services} picked={picked} toggle={toggle} strip={named > 1 ? box.name : ""} />
        </div>))}
    </div>
  );
}

function ServiceItems({ items, services, picked, toggle, strip }) {
  return (
    <>
      {items.map((s) => {
        const on = picked.includes(s.id);
        const off = !on && s.group && services.some((x) => x.group === s.group && picked.includes(x.id) && (x.kind !== s.kind || s.kind !== "single"));
        return (
          <button key={s.id} type="button" onClick={() => toggle(s)} data-testid={`booking-service-${s.id}`} aria-disabled={off}
            className={`w-full flex items-center gap-3 p-3 rounded-2xl border-2 text-start transition-[opacity,border-color,background-color] ${on ? "border-[#1A5C3A] bg-[#EEF5F0]" : off ? "border-[#EFEBE0] bg-slate-50 opacity-45" : "border-[#EFEBE0] bg-white hover:border-slate-300"}`}>
            <span className={`w-5 h-5 ${s.kind === "single" ? "rounded-md" : "rounded-full"} border-2 flex items-center justify-center shrink-0 ${on ? "bg-[#1A5C3A] border-[#1A5C3A]" : "border-slate-300"}`}>{on && <Check className="w-3.5 h-3.5 text-white" />}</span>
            <span className="flex-1 text-sm font-bold text-slate-800">{strip && s.label.startsWith(`${strip}: `) ? s.label.slice(strip.length + 2) : s.label}</span>
            <span className="text-sm font-extrabold text-[#1A5C3A] text-end">{s.originalText && <s className="block text-[11px] text-slate-400 font-semibold">{s.originalText}</s>}{s.priceText}</span>
          </button>
        );
      })}
    </>
  );
}

function DateStrip({ dates, date, setDate, lang }) {
  return (
    <div className="flex gap-2 overflow-x-auto pb-1 -mx-1 px-1" data-testid="booking-dates">
      {dates.map((d) => {
        const dt = new Date(`${d.date}T00:00:00`);
        const on = date === d.date;
        return (
          <button key={d.date} type="button" disabled={!d.open} onClick={() => setDate(d.date)} data-testid={`booking-date-${d.date}`}
            className={`shrink-0 w-16 py-2 rounded-2xl border-2 text-center transition-colors disabled:opacity-35 ${on ? "bg-[#1A5C3A] border-[#1A5C3A] text-white" : "bg-white border-[#EFEBE0] hover:border-[#1A5C3A]"}`}>
            <span className="block text-[11px] font-bold">{dt.toLocaleDateString(lang === "en" ? "en-GB" : "ar-AE-u-nu-latn", { weekday: "short" })}</span>
            <span className="block text-lg font-extrabold">{dt.getDate()}</span>
            <span className="block text-[10px]">{dt.toLocaleDateString(lang === "en" ? "en-GB" : "ar-AE-u-nu-latn", { month: "short" })}</span>
          </button>
        );
      })}
    </div>
  );
}

// "حجز موعد" — approved home-services ads only. Working hours are shown here (not in the details table).
export function BookingSection({ listing, lang, mine }) {
  const L = (ar, en) => (lang === "en" ? en : ar);
  const navigate = useNavigate();
  const { user } = useAuth();
  const { data: meta } = useCachedGet("/meta");
  const [info, setInfo] = useState(null);
  const [date, setDate] = useState(null);
  const [slots, setSlots] = useState(null);
  const [time, setTime] = useState(null);
  const [picked, setPicked] = useState([]);
  const [form, setForm] = useState(false);
  const services = useMemo(() => (meta ? bookingServices(meta, listing, lang) : []), [meta, listing, lang]);
  useEffect(() => { api.get(`/listings/${listing.id}/booking-slots`).then(({ data }) => setInfo(data)).catch(() => setInfo(false)); }, [listing.id]);
  const loadSlots = (d) => { setSlots(null); setTime(null); api.get(`/listings/${listing.id}/booking-slots`, { params: { date: d } }).then(({ data }) => setSlots(data.slots)).catch(() => setSlots([])); };
  useEffect(() => { if (date) loadSlots(date); }, [date]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!info) return null;
  // Per group: many individual services OR the all-services price OR the exclusive offer — switching clears the others.
  const toggle = (s) => setPicked((p) => (p.includes(s.id) ? p.filter((x) => x !== s.id)
    : [...p.filter((x) => { const o = services.find((y) => y.id === x); return !s.group || o?.group !== s.group || (o.kind === "single" && s.kind === "single"); }), s.id]));
  const canBook = time && (picked.length > 0 || !services.length);
  const proceed = () => (user ? setForm(true) : navigate("/login"));
  const h = info.hours;
  return (
    <section className="bg-white rounded-3xl border border-[#EFEBE0] p-5 space-y-5" data-testid="booking-section">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 className="font-headline text-lg font-extrabold flex items-center gap-2"><CalendarDays className="w-5 h-5 text-[#1A5C3A]" />{L("حجز موعد", "Book an appointment")}</h2>
          {h && <p className="text-xs text-slate-500 mt-1.5 flex items-center gap-1.5" data-testid="booking-hours"><Clock className="w-3.5 h-3.5" />{daysLabel(h.days, lang)} · <span dir="ltr">{fmtTime(h.start, lang)} — {fmtTime(h.end, lang)}</span> · {L("بتوقيت الإمارات", "UAE time")}</p>}
        </div>
        <span className={`shrink-0 px-3 py-1 rounded-full text-xs font-bold ${info.enabled ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-500"}`} data-testid="booking-availability">
          {info.enabled ? L("متاح للحجز", "Available for booking") : L("غير متاح للحجز", "Not available for booking")}</span>
      </div>
      {info.enabled && !mine && <>
        {services.length > 0 && <Step n={1} title={L("اختر الخدمة", "Choose the service")}><ServicePicker services={services} picked={picked} toggle={toggle} L={L} /></Step>}
        <Step n={services.length ? 2 : 1} title={L("اختر اليوم", "Choose the day")}><DateStrip dates={info.dates} date={date} setDate={setDate} lang={lang} /></Step>
        {date && <Step n={services.length ? 3 : 2} title={L("اختر الوقت (بتوقيت الإمارات)", "Choose the time (UAE time)")}>
          {!slots ? <Loader2 className="w-5 h-5 animate-spin text-[#1A5C3A]" /> : !slots.some((s) => s.available) ? <p className="text-sm text-slate-500" data-testid="booking-no-slots">{L("لا توجد مواعيد متاحة في هذا اليوم", "No available times on this day")}</p> : (
            <div className="grid grid-cols-3 sm:grid-cols-4 gap-2" data-testid="booking-slots">
              {slots.map((s) => (
                <button key={s.time} type="button" disabled={!s.available} onClick={() => setTime(s.time)} data-testid={`booking-slot-${s.time}`}
                  className={`h-11 rounded-xl border-2 text-sm font-bold transition-colors disabled:opacity-35 disabled:line-through ${time === s.time ? "bg-[#1A5C3A] border-[#1A5C3A] text-white" : "bg-white border-[#EFEBE0] hover:border-[#1A5C3A]"}`} dir="ltr">{fmtTime(s.time, lang)}</button>))}
            </div>)}
        </Step>}
        <button disabled={!canBook} onClick={proceed} className="w-full h-13 py-3.5 rounded-2xl bg-[#1A5C3A] hover:bg-[#154a2f] text-white font-extrabold disabled:opacity-40 transition-colors" data-testid="booking-continue">
          {!services.length || picked.length ? (time ? L("متابعة الحجز", "Continue booking") : L("اختر اليوم والوقت", "Pick a day and time")) : L("اختر خدمة واحدة على الأقل", "Choose at least one service")}</button>
      </>}
      {form && <Suspense fallback={null}><BookingForm listing={listing} user={user} picked={services.filter((s) => picked.includes(s.id))} date={date} time={time} L={L} lang={lang}
        onClose={() => setForm(false)} onDone={() => { setTime(null); loadSlots(date); }} /></Suspense>}
    </section>
  );
}
